import {useTranslation} from "react-i18next";
import AuthLayout from "../../layout/AuthLayout.jsx";
import AuthImage from "../../mosaic/images/auth-image.jpg";
import FormContainer from "../../components/form/FormContainer.jsx";
import FormControl from "../../components/form/FormControl.jsx";
import {useContext, useEffect, useState} from "react";
import FormCheckbox from "../../components/form/FormCheckbox.jsx";
import useDownloadState from "../../api/utilities/useDownloadState.js";
import FormButton from "../../components/form/FormButton.jsx";
import LoadingComponent from "../../components/LoadingComponent.jsx";
import {AuthenticationApi} from "../../api/authority/generated/index.ts";
import FormError from "../../components/form/FormError.jsx";
import {GlobalContext} from "../../utils/context/GlobalContextProvider.jsx";
import {useNavigate} from "react-router-dom";
import {isPreview, isDevelopment} from "../../utils/environment.js";

export default function SignUp() {
    const {t} = useTranslation();

    const [buttonClicked, setButtonClicked] = useState(false);
    const [signUpState, setSignUpLoading, setSignUpResult, setSignUpError] = useDownloadState();

    let navigate = useNavigate();

    let signUpCredentialsInitialState = {
        email: '',
        password: '',
        passwordAgain: '',
        agreementTermsOfService: false,
        agreementPrivacyPolicy: false,
    };

    if (isDevelopment() || isPreview()) {
        // TODO: multiple account types clickable in development mode.
        signUpCredentialsInitialState = {
            email: 'demo@datixo.com',
            password: 'demo',
            passwordAgain: 'demo',
            agreementTermsOfService: true,
            agreementPrivacyPolicy: true,
        };
    }

    /** @var {SignUpCredentials} signUpCredentials */
    const [signUpCredentials, setSignUpCredentials] = useState(signUpCredentialsInitialState);

    const startSignUpProcess = () => {
        setButtonClicked(true);
    };

    let {setCurrentIdentity, getAuthorityConfiguration} = useContext(GlobalContext);

    let authorityConfiguration = getAuthorityConfiguration();

    useEffect(() => {
        if (!buttonClicked || signUpState.downloading) {
            return;
        }

        setButtonClicked(false);
        setSignUpLoading(true);
        setSignUpError(null);

        if (
            signUpCredentials.password !== '' && signUpCredentials.passwordAgain !== ''
            && signUpCredentials.password !== signUpCredentials.passwordAgain
        ) {
            setSignUpError({message: t('auth.passwords-must-match')});
            setSignUpLoading(false);
            return;
        }

        new AuthenticationApi(authorityConfiguration)
            .signUpRaw({
                signUpCredentials: signUpCredentials,
            })
            .then((signInTokenResponseBody) => {
                if (signInTokenResponseBody.raw.status === 200) {
                    signInTokenResponseBody.raw.json().then(() => {
                        navigate('/sign/up/continue-with-email');
                    });
                }

                if (signInTokenResponseBody.raw.status === 201) {
                    signInTokenResponseBody.raw.json().then((signInTokenResponseBody) => {
                        setCurrentIdentity(signInTokenResponseBody);
                        navigate('/');
                    });
                }
            })
            .catch((error) => setSignUpError(error))
            .finally(() => setSignUpLoading(false));
    }, [buttonClicked]);

    return (
        <AuthLayout title={t('auth.sign-up')} authImage={AuthImage}>
            <LoadingComponent loading={signUpState.downloading}>
                <FormContainer>
                    {signUpState.error && <FormError error={signUpState.error}/>}

                    <FormControl
                        label={t('auth.email-address')}
                        type={'email'}
                        value={signUpCredentials.email}
                        onChange={(e) => setSignUpCredentials({...signUpCredentials, email: e.target.value})}
                    />

                    <div className="mt-4">
                        <FormControl
                            label={t('auth.password')}
                            type={'password'}
                            value={signUpCredentials.password}
                            onChange={(e) => setSignUpCredentials({...signUpCredentials, password: e.target.value})}
                        />
                    </div>

                    <div className="mt-4">
                        <FormControl
                            label={t('auth.password-again')}
                            type={'password'}
                            value={signUpCredentials.passwordAgain}
                            onChange={(e) => setSignUpCredentials({
                                ...signUpCredentials,
                                passwordAgain: e.target.value
                            })}
                        />
                    </div>

                    <FormCheckbox
                        label={t('auth.agreed-with-tos-with-link', {link: 'https://datixo.com/documents/vseobecne-obchodni-podminky.pdf'})}
                        checked={signUpCredentials.agreementTermsOfService}
                        onChange={(e) => setSignUpCredentials({
                            ...signUpCredentials,
                            agreementTermsOfService: e.target.checked
                        })}
                    />

                    <FormCheckbox
                        label={t('auth.agreed-with-privacy-with-link', {link: 'https://datixo.com/documents/vseobecne-obchodni-podminky.pdf'})}
                        checked={signUpCredentials.agreementPrivacyPolicy}
                        onChange={(e) => setSignUpCredentials({
                            ...signUpCredentials,
                            agreementPrivacyPolicy: e.target.checked
                        })}
                    />

                    <div className="auth-btn-wrap">
                        <FormButton
                            onClick={() => startSignUpProcess()}
                            className={'button-primary'}
                        >
                            {t('auth.sign-up-button')}
                        </FormButton>
                    </div>

                </FormContainer>
            </LoadingComponent>
        </AuthLayout>
    );
}